import React, { useState, useCallback } from 'react';

/*
  CashFlowReality — v1 uses bank statement upload or manual entry.
  Architecture note: data fetching is isolated in fetchCashFlowData() so an
  Account Aggregator source can be swapped in later without touching the
  insight/display layer.
*/

const fmtRs = n => `Rs.\u200b${Math.round(Math.abs(n)).toLocaleString('en-IN')}`;

// ── Data source abstraction ───────────────────────────────────────────────────
// v1: returns data from manual input or from the uploaded + parsed profile.
// Future: replace this function with an AA connector — the rest of the page
// does not need to change.
async function fetchCashFlowData(source, payload) {
  if (source === 'manual') {
    return {
      monthlyIncome: payload.income,
      fixedObligations: payload.fixed,
      variableExpenses: payload.variable,
      savingsBalance: payload.savings,
      transactionNotes: payload.notes,
    };
  }
  if (source === 'profile') {
    const res = await fetch('/api/finance?action=profile', { credentials: 'include' });
    if (!res.ok) return null;
    const data = await res.json();
    const p = data?.profile;
    if (!p) return null;
    return {
      monthlyIncome: p.monthlyIncome || 0,
      fixedObligations: p.monthlyExpenses * 0.5 || 0, // rough split; profile doesn't break down
      variableExpenses: p.monthlyExpenses * 0.5 || 0,
      savingsBalance: p.currentBalance || 0,
    };
  }
  return null;
}

const CashFlowReality = ({ user, navigateTo, goBack }) => {
  const [inputMethod, setInputMethod] = useState('manual'); // 'manual' | 'upload'
  const [uploadState, setUploadState] = useState('idle'); // 'idle' | 'uploading' | 'done' | 'error'
  const [uploadError, setUploadError] = useState('');
  const [dragOver, setDragOver] = useState(false);

  // Manual input
  const [income, setIncome] = useState('');
  const [fixed, setFixed] = useState('');       // rent + EMIs
  const [variable, setVariable] = useState(''); // food, bills, subs
  const [savings, setSavings] = useState('');
  const [notes, setNotes] = useState('');       // any recurring leaks user wants to flag

  // Analysis state
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [analysisError, setAnalysisError] = useState('');
  const [mode, setMode] = useState('input'); // 'input' | 'result'

  // ── Upload handler ──────────────────────────────────────────────────────────
  const handleFileUpload = useCallback(async (file) => {
    if (!user) { navigateTo('signup'); return; }
    if (!file) return;
    const allowed = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'text/csv', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
    if (!allowed.includes(file.type)) {
      setUploadError('Please upload a PDF, image (JPG/PNG), CSV, or XLSX bank statement.');
      return;
    }
    setUploadState('uploading');
    setUploadError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('docType', 'bank_statement');
      const res = await fetch('/api/finance?action=upload', { method: 'POST', credentials: 'include', body: formData });
      if (!res.ok) throw new Error('Upload failed');
      setUploadState('done');
      // After upload, fetch profile to populate fields
      const profileData = await fetchCashFlowData('profile', {});
      if (profileData?.monthlyIncome) {
        setIncome(String(Math.round(profileData.monthlyIncome)));
        setFixed(String(Math.round(profileData.fixedObligations)));
        setVariable(String(Math.round(profileData.variableExpenses)));
        if (profileData.savingsBalance) setSavings(String(Math.round(profileData.savingsBalance)));
      }
    } catch (err) {
      setUploadState('error');
      setUploadError('Upload failed. Please try again or enter numbers manually.');
    }
  }, [user, navigateTo]);

  // ── Analysis call ───────────────────────────────────────────────────────────
  const runAnalysis = async () => {
    const inc  = Number(income)   || 0;
    const fix  = Number(fixed)    || 0;
    const varE = Number(variable) || 0;
    if (!inc) { setAnalysisError('Monthly income is required.'); return; }

    setAnalysisLoading(true);
    setAnalysisError('');
    try {
      const res = await fetch('/api/finance?action=cashflow-reality', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ monthlyIncome: inc, fixedObligations: fix, variableExpenses: varE, savingsBalance: Number(savings) || 0, transactionNotes: notes }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Analysis failed');
      setAnalysisResult(data);
      setMode('result');
    } catch (err) {
      // Fallback: compute client-side if API not available
      const disc = inc - fix - varE;
      const lockedPct = inc > 0 ? Math.round((fix / inc) * 100) : 0;
      setAnalysisResult({
        discretionaryIncome: disc,
        lockedPercentage: lockedPct,
        insights: [
          {
            heading: 'True discretionary income',
            insight: `${lockedPct}% of your income is locked in fixed obligations. Your actual free money each month is ${fmtRs(disc)}, not your total income or bank balance.`,
            action: fix > inc * 0.5 ? 'Your fixed obligations exceed 50% of income — identify the largest item and check if it can be reduced (e.g. prepay a loan, renegotiate rent).' : 'Your fixed cost ratio is healthy. The next step is tightening variable spend.',
          },
          ...(Number(savings) > 0 && inc > 0 ? [{
            heading: 'Emergency fund status',
            insight: `At ${fmtRs(Number(savings))} saved against monthly expenses of ${fmtRs(fix + varE)}, you have ${((Number(savings)) / (fix + varE || 1)).toFixed(1)} months of runway.`,
            action: (Number(savings)) / (fix + varE || 1) < 3 ? `Build to 3 months of expenses (${fmtRs((fix + varE) * 3)}) before adding any discretionary commitments.` : 'Emergency fund looks adequate. Consider directing surplus to long-term goals.',
          }] : []),
          ...(disc > 0 ? [{
            heading: 'What to do with the surplus',
            insight: `You have ${fmtRs(disc)}/month after all obligations. Over a year that's ${fmtRs(disc * 12)}.`,
            action: 'If you haven\'t started a recurring investment (SIP), even Rs.1,000–5,000/month directed to an index fund compounds significantly over time. This is a category nudge — pick an instrument that matches your risk horizon.',
          }] : [{
            heading: 'Negative discretionary income',
            insight: `Your fixed + variable expenses exceed your income by ${fmtRs(Math.abs(disc))}/month. This is unsustainable.`,
            action: 'List all fixed obligations and identify the top 2 by amount. Renegotiating or prepaying one of them may be the single highest-leverage action right now.',
          }]),
        ],
      });
      setMode('result');
    }
    setAnalysisLoading(false);
  };

  const inp = {
    width: '100%', padding: '10px 12px', borderRadius: '8px',
    border: '1px solid var(--border)', backgroundColor: 'var(--bg)', color: 'var(--text-primary)',
    fontSize: '14px', outline: 'none', transition: 'border-color 0.2s',
    boxSizing: 'border-box', fontFamily: 'inherit',
  };
  const lbl = { display: 'block', fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.06em' };
  const focusIn = e => { e.target.style.borderColor = 'var(--accent)'; };
  const blurIn  = e => { e.target.style.borderColor = 'var(--border)'; };

  const disc = (Number(income) || 0) - (Number(fixed) || 0) - (Number(variable) || 0);
  const lockedPct = Number(income) > 0 ? Math.round((Number(fixed) / Number(income)) * 100) : 0;

  return (
    <section style={{ minHeight: '100vh', backgroundColor: 'var(--bg-page)', paddingTop: '80px' }}>
      <div style={{ maxWidth: '720px', margin: '0 auto', padding: '40px 24px 80px' }}>

        <button onClick={goBack} style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', fontSize: '13px', cursor: 'pointer', padding: '0 0 16px', fontFamily: 'inherit' }}>← Back</button>

        <div style={{ marginBottom: '28px' }}>
          <p style={{ fontSize: '11px', fontWeight: 700, color: '#F59E0B', textTransform: 'uppercase', letterSpacing: '0.12em', margin: '0 0 8px' }}>Cash-Flow Reality</p>
          <h1 style={{ fontSize: '28px', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 8px', letterSpacing: '-0.02em' }}>What's your real discretionary income?</h1>
          <p style={{ fontSize: '15px', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.6 }}>
            Not your bank balance. Not your salary. What you actually control each month after all obligations.
          </p>
        </div>

        {mode === 'input' && (
          <>
            {/* Input method tabs */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
              {[{ val: 'manual', label: 'Enter numbers' }, { val: 'upload', label: 'Upload bank statement' }].map(opt => (
                <button key={opt.val} onClick={() => setInputMethod(opt.val)} style={{
                  padding: '8px 16px', borderRadius: '20px', fontSize: '13px', fontWeight: 500,
                  cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
                  backgroundColor: inputMethod === opt.val ? '#F59E0B' : 'transparent',
                  color: inputMethod === opt.val ? '#fff' : 'var(--text-secondary)',
                  border: `1px solid ${inputMethod === opt.val ? '#F59E0B' : 'var(--border)'}`,
                }}>{opt.label}</button>
              ))}
            </div>

            {/* Upload area */}
            {inputMethod === 'upload' && (
              <div style={{ marginBottom: '20px' }}>
                {!user && (
                  <div style={{ padding: '16px 20px', backgroundColor: 'rgba(0,194,124,0.06)', border: '1px solid rgba(0,194,124,0.2)', borderRadius: '10px', marginBottom: '14px' }}>
                    <p style={{ fontSize: '14px', color: 'var(--text-primary)', margin: '0 0 8px', fontWeight: 600 }}>Sign in to upload documents</p>
                    <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '0 0 12px' }}>Your bank statement is parsed securely and never stored as a file — only the extracted numbers.</p>
                    <button onClick={() => navigateTo('signup')} style={{ backgroundColor: 'var(--accent)', color: '#fff', border: 'none', borderRadius: '7px', padding: '9px 18px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Create free account →</button>
                  </div>
                )}
                {user && (
                  <div
                    onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={e => { e.preventDefault(); setDragOver(false); handleFileUpload(e.dataTransfer.files[0]); }}
                    onClick={() => document.getElementById('cfr-file-input').click()}
                    style={{
                      border: `2px dashed ${dragOver ? '#D97706' : uploadState === 'done' ? 'var(--accent)' : 'var(--border-strong)'}`,
                      borderRadius: 'var(--radius-card)', padding: '40px', textAlign: 'center', cursor: 'pointer',
                      backgroundColor: dragOver ? 'rgba(217,119,6,0.04)' : uploadState === 'done' ? 'var(--accent-subtle)' : 'var(--bg)',
                      transition: 'all var(--t-base)', marginBottom: '12px',
                      boxShadow: 'var(--shadow-xs)',
                    }}
                  >
                    <input id="cfr-file-input" type="file" accept=".pdf,.jpg,.jpeg,.png,.csv,.xlsx" style={{ display: 'none' }} onChange={e => handleFileUpload(e.target.files[0])} />
                    {uploadState === 'idle' && <>
                      <p style={{ fontSize: '32px', margin: '0 0 8px' }}>📄</p>
                      <p style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 4px' }}>Drag & drop your bank statement</p>
                      <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: 0 }}>PDF, image, CSV, or XLSX · Never stored as a file — only the numbers are extracted</p>
                    </>}
                    {uploadState === 'uploading' && <>
                      <p style={{ fontSize: '32px', margin: '0 0 8px' }}>⏳</p>
                      <p style={{ fontSize: '14px', color: 'var(--text-secondary)', margin: 0 }}>Parsing your statement…</p>
                    </>}
                    {uploadState === 'done' && <>
                      <p style={{ fontSize: '32px', margin: '0 0 8px' }}>✅</p>
                      <p style={{ fontSize: '14px', fontWeight: 600, color: 'var(--accent)', margin: '0 0 4px' }}>Statement parsed — numbers filled below</p>
                      <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: 0 }}>Review and adjust the fields if needed, then run the analysis.</p>
                    </>}
                    {uploadState === 'error' && <>
                      <p style={{ fontSize: '32px', margin: '0 0 8px' }}>⚠️</p>
                      <p style={{ fontSize: '14px', color: '#EF4444', margin: 0 }}>{uploadError}</p>
                    </>}
                  </div>
                )}
              </div>
            )}

            {/* Manual / review fields */}
            <div style={{ backgroundColor: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', padding: '28px', marginBottom: '14px', boxShadow: 'var(--shadow-card)' }}>
              <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 20px' }}>
                {uploadState === 'done' ? 'Review extracted numbers' : 'Your monthly cash flow'}
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={lbl}>Monthly take-home income (₹)</label>
                  <input type="number" value={income} onChange={e => setIncome(e.target.value)} placeholder="e.g. 85000" style={inp} onFocus={focusIn} onBlur={blurIn} />
                </div>
                <div>
                  <label style={lbl}>Fixed obligations — rent + EMIs (₹/mo)</label>
                  <input type="number" value={fixed} onChange={e => setFixed(e.target.value)} placeholder="e.g. 40000" style={inp} onFocus={focusIn} onBlur={blurIn} />
                </div>
                <div>
                  <label style={lbl}>Variable expenses — food, bills, subs (₹/mo)</label>
                  <input type="number" value={variable} onChange={e => setVariable(e.target.value)} placeholder="e.g. 20000" style={inp} onFocus={focusIn} onBlur={blurIn} />
                </div>
                <div>
                  <label style={lbl}>Savings / accessible balance (₹)</label>
                  <input type="number" value={savings} onChange={e => setSavings(e.target.value)} placeholder="e.g. 150000" style={inp} onFocus={focusIn} onBlur={blurIn} />
                </div>
              </div>

              <div style={{ marginTop: '12px' }}>
                <label style={lbl}>Any recurring leaks you've noticed? (optional — helps the analysis)</label>
                <input type="text" value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. Rs.4k unused gym, Rs.2k streaming subscriptions…" style={inp} onFocus={focusIn} onBlur={blurIn} />
              </div>

              {/* Live preview */}
              {Number(income) > 0 && (
                <div style={{ marginTop: '16px', padding: '16px', backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', borderLeft: `2px solid ${disc >= 0 ? 'var(--accent)' : 'var(--red)'}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                    <div>
                      <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 4px' }}>True discretionary income</p>
                      <p style={{ fontSize: '22px', fontWeight: 700, color: disc >= 0 ? 'var(--accent)' : '#EF4444', margin: 0, letterSpacing: '-0.01em' }}>{fmtRs(disc)}<span style={{ fontSize: '13px', fontWeight: 400, color: 'var(--text-tertiary)' }}>/month</span></p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 4px' }}>Fixed obligation load</p>
                      <p style={{ fontSize: '22px', fontWeight: 700, color: lockedPct > 50 ? '#F59E0B' : 'var(--text-primary)', margin: 0 }}>{lockedPct}%<span style={{ fontSize: '13px', fontWeight: 400, color: 'var(--text-tertiary)' }}> of income</span></p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {analysisError && (
              <p style={{ fontSize: '13px', color: '#EF4444', margin: '0 0 14px' }}>{analysisError}</p>
            )}

            <button
              onClick={runAnalysis}
              disabled={!income || analysisLoading}
              style={{
                width: '100%', backgroundColor: '#F59E0B', color: '#fff', border: 'none',
                borderRadius: '10px', padding: '14px', fontSize: '15px', fontWeight: 600,
                cursor: !income || analysisLoading ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit', opacity: !income || analysisLoading ? 0.7 : 1, transition: 'opacity 0.2s',
              }}
            >{analysisLoading ? 'Analysing…' : 'Show me the reality →'}</button>
          </>
        )}

        {/* ── RESULT ──────────────────────────────────────────────────────────── */}
        {mode === 'result' && analysisResult && (
          <>
            {/* Discretionary income headline */}
            <div style={{
              backgroundColor: 'var(--bg)',
              border: '1px solid var(--border)',
              borderLeft: `3px solid ${analysisResult.discretionaryIncome >= 0 ? 'var(--accent)' : 'var(--red)'}`,
              borderRadius: 'var(--radius-card)', padding: '28px 32px', marginBottom: '16px',
              boxShadow: 'var(--shadow-sm)',
            }}>
              <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 8px' }}>Your real picture</p>
              <p style={{ fontSize: '15px', color: 'var(--text-secondary)', margin: '0 0 6px', lineHeight: 1.6 }}>
                {analysisResult.lockedPercentage}% of your income is locked in fixed obligations.
                Your true discretionary income is:
              </p>
              <p style={{ fontSize: '48px', fontWeight: 800, color: analysisResult.discretionaryIncome >= 0 ? 'var(--accent)' : 'var(--red)', margin: 0, letterSpacing: '-0.04em', lineHeight: 0.95, fontVariantNumeric: 'tabular-nums' }}>
                {fmtRs(analysisResult.discretionaryIncome)}<span style={{ fontSize: '16px', fontWeight: 500, color: 'var(--text-tertiary)', letterSpacing: 0, lineHeight: 1 }}>/mo</span>
              </p>
              <p style={{ fontSize: '13px', color: 'var(--text-tertiary)', margin: '8px 0 0' }}>
                — not the {fmtRs(Number(income))} income or the {savings ? fmtRs(Number(savings)) : 'balance'} in your account.
              </p>
            </div>

            {/* Insights */}
            {analysisResult.insights?.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
                {analysisResult.insights.map((ins, i) => (
                  <div key={i} style={{ backgroundColor: 'var(--bg)', border: '1px solid var(--border)', borderLeft: '3px solid #D97706', borderRadius: 'var(--radius-card)', padding: '20px 24px', boxShadow: 'var(--shadow-card)' }}>
                    <p style={{ fontSize: '11px', fontWeight: 700, color: '#B45309', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 8px' }}>{ins.heading}</p>
                    <p style={{ fontSize: '14px', color: 'var(--text-primary)', margin: '0 0 10px', lineHeight: 1.65 }}>{ins.insight}</p>
                    <div style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '12px 14px' }}>
                      <p style={{ fontSize: '11px', fontWeight: 700, color: '#B45309', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 4px' }}>So here's what to do</p>
                      <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.6 }}>{ins.action}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div style={{ padding: '14px 16px', backgroundColor: 'rgba(0,0,0,0.02)', border: '1px solid var(--border)', borderRadius: '8px', marginBottom: '20px' }}>
              <p style={{ fontSize: '12px', color: 'var(--text-tertiary)', margin: 0, lineHeight: 1.65 }}>
                Analysis is based on the numbers you provided. Category nudges (e.g. "start an SIP") refer to investment categories, not specific products or funds. This is decision support — consult a SEBI-registered advisor for personalised product recommendations.
              </p>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setMode('input')} style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--text-secondary)', borderRadius: '8px', padding: '10px 20px', fontSize: '13px', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }}>← Adjust numbers</button>
              {user && (
                <button onClick={() => navigateTo('life-finance')} style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--accent)', borderRadius: '8px', padding: '10px 20px', fontSize: '13px', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }}>Go to Finance Dashboard →</button>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default CashFlowReality;
